import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { clarityEvent, gaEvent } from '../analytics';
import { submitNewsletterSubscribe } from '../services/contactService';
import styles from './StudioFooter.module.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = Boolean(RECAPTCHA_SITE_KEY);

export default function StudioFooter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaError, setRecaptchaError] = useState('');
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [popup, setPopup] = useState({ isOpen: false, phase: 'loading', message: '' });
  const recaptchaRef = useRef(null);
  const popupButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const isRecaptchaTimeout = useCallback((err) => {
    const msg = err?.message || err?.toString?.() || '';
    return typeof msg === 'string' && msg.toLowerCase().includes('timeout');
  }, []);

  const safeResetRecaptcha = useCallback((context) => {
    try {
      recaptchaRef.current?.reset?.();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('Ignoring studio footer reCAPTCHA reset error', context, err);
      }
    }
    setRecaptchaToken('');
  }, []);

  useEffect(() => {
    return () => {
      safeResetRecaptcha('cleanup');
      setRecaptchaToken('');
    };
  }, [safeResetRecaptcha]);

  // Focus trap + Escape key for popup
  useEffect(() => {
    if (popup.isOpen) {
      previousFocusRef.current = document.activeElement;
      const timer = setTimeout(() => popupButtonRef.current?.focus(), 80);
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          handlePopupOk();
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          popupButtonRef.current?.focus();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKeyDown);
      };
    } else if (previousFocusRef.current) {
      previousFocusRef.current?.focus?.();
      previousFocusRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup.isOpen]);

  const trackFooterLink = (label, href) => {
    gaEvent('click_footer_link', { link_label: label, href });
    clarityEvent('click_footer_link');
  };

  const handleFooterLogoClick = () => {
    gaEvent('click_studio_logo', { href: '/' });
    clarityEvent('click_studio_logo');
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setRecaptchaError('');

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (!RECAPTCHA_ENABLED) {
      setRecaptchaError('reCAPTCHA is not configured.');
      gaEvent('recaptcha_error', { reason: 'studio_newsletter_not_configured' });
      clarityEvent('recaptcha_error');
      return;
    }

    if (!recaptchaToken) {
      setShowRecaptcha(true);
      setRecaptchaError('Please complete the reCAPTCHA.');
      gaEvent('recaptcha_error', { reason: 'studio_newsletter_missing_token' });
      clarityEvent('recaptcha_error');
      return;
    }

    try {
      setStatus('submitting');
      setPopup({ isOpen: true, phase: 'loading', message: 'Subscribing\u2026' });

      await submitNewsletterSubscribe({ email: normalizedEmail, recaptchaToken });

      gaEvent('generate_lead', { page_section: 'studio_footer_newsletter' });
      clarityEvent('studio_footer_newsletter_success');

      setStatus('success');
      setPopup({
        isOpen: true,
        phase: 'success',
        message: "We\u2019ll keep you updated as we get closer to launch.",
      });
      setEmail('');
      setRecaptchaError('');
      safeResetRecaptcha('newsletter-success');
    } catch (err) {
      setStatus('error');
      setPopup({
        isOpen: true,
        phase: 'error',
        message: err?.message || 'Could not subscribe right now. Please try again.',
      });
      gaEvent('form_error', { form_name: 'studio_footer_newsletter' });
      clarityEvent('studio_footer_newsletter_error');
    }
  };

  const handlePopupOk = () => {
    setPopup({ isOpen: false, phase: 'loading', message: '' });
    setStatus('idle');
    setRecaptchaError('');
    setShowRecaptcha(false);
    safeResetRecaptcha('popup-ok');
  };

  const renderPopupMessage = (message) => {
    const lines = message.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={`${line}-${index}`}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.brand}>
          <Link
            to="/"
            className={styles.logoLink}
            onClick={handleFooterLogoClick}
          >
            <img
              src="/images/webp/logo.webp"
              srcSet="/images/webp/logo_192w.webp 192w, /images/webp/logo.webp 357w"
              sizes="153px"
              alt="StratMech Studios"
              className={styles.logoImage}
              width={357}
              height={245}
            />
          </Link>
        </div>

        {/* Newsletter subscribe */}
        <div className={styles.newsletterSection}>
          <h3 className={styles.newsletterHeading}>Stay in the loop</h3>
          <p className={styles.newsletterSubtext}>
            Subscribe for updates on releases, development notes, and more.
          </p>
          <form className={styles.newsletterForm} onSubmit={handleNewsletterSubmit}>
            <input
              type="email"
              value={email}
              onFocus={() => setShowRecaptcha(true)}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              placeholder="Your email address"
              className={styles.newsletterInput}
              required
              aria-label="Email address for newsletter"
            />
            <button
              type="submit"
              className={styles.newsletterButton}
              disabled={status === 'submitting' || !RECAPTCHA_ENABLED}
            >
              Subscribe
            </button>
          </form>
          {errorMessage && <p className={styles.newsletterError}>{errorMessage}</p>}
          {RECAPTCHA_ENABLED && showRecaptcha ? (
            <div className={styles.newsletterRecaptcha}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => {
                  setRecaptchaToken(token || '');
                  if (recaptchaError) setRecaptchaError('');
                }}
                onExpired={() => {
                  setRecaptchaToken('');
                  setRecaptchaError('reCAPTCHA expired. Please retry.');
                  gaEvent('recaptcha_error', { reason: 'studio_footer_expired' });
                  clarityEvent('recaptcha_error');
                }}
                onErrored={(err) => {
                  setRecaptchaToken('');
                  setRecaptchaError(
                    isRecaptchaTimeout(err)
                      ? 'reCAPTCHA timed out. Please retry.'
                      : 'reCAPTCHA failed to load. Please retry.'
                  );
                  gaEvent('recaptcha_error', { reason: isRecaptchaTimeout(err) ? 'studio_footer_timeout' : 'studio_footer_load_failed' });
                  clarityEvent('recaptcha_error');
                }}
                theme="light"
              />
            </div>
          ) : !RECAPTCHA_ENABLED ? (
            <p className={styles.newsletterError}>reCAPTCHA is unavailable right now.</p>
          ) : null}
          {recaptchaError && <p className={styles.newsletterError}>{recaptchaError}</p>}
        </div>

        <div className={styles.linksGrid}>
          <div className={styles.column}>
            <h3 className={styles.columnHeading}>Connect</h3>
            <ul className={styles.linkList}>
              <li><a href="/#partner" onClick={() => trackFooterLink('Partner', '/#partner')}>Partner</a></li>
              <li><Link to="/contact" onClick={() => trackFooterLink('Contact', '/contact')}>Contact</Link></li>
              <li><a href="https://www.instagram.com/stratmechstudios/" target="_blank" rel="noopener noreferrer" onClick={() => trackFooterLink('Instagram', 'https://www.instagram.com/stratmechstudios/')}>Follow us on Instagram</a></li>
            </ul>
          </div>
          <div className={styles.column}>
            <h3 className={styles.columnHeading}>Orders</h3>
            <ul className={styles.linkList}>
              <li><Link to="/shipping" onClick={() => trackFooterLink('Shipping', '/shipping')}>Shipping</Link></li>
              <li><Link to="/refund-cancellation-policy" onClick={() => trackFooterLink('Refund & Cancellation', '/refund-cancellation-policy')}>Refund &amp; Cancellation</Link></li>
            </ul>
          </div>
          <div className={styles.column}>
            <h3 className={styles.columnHeading}>Legal</h3>
            <ul className={styles.linkList}>
              <li><Link to="/privacy-policy" onClick={() => trackFooterLink('Privacy Policy', '/privacy-policy')}>Privacy Policy</Link></li>
              <li><Link to="/ip-policy" onClick={() => trackFooterLink('IP Policy', '/ip-policy')}>IP Policy</Link></li>
              <li><Link to="/terms-of-service" onClick={() => trackFooterLink('Terms of Service', '/terms-of-service')}>Terms of Service</Link></li>
            </ul>
          </div>
        </div>

        {/* Contact line */}
        <div className={styles.contactLine}>
          <p>
            If you have a question, an idea, or feedback to share, we&apos;d love to hear from you.
            {' '}Please reach out to us at{' '}
            <a
              href="mailto:info@stratmechstudios.com"
              className={styles.contactEmail}
              onClick={() => {
                gaEvent('click_contact', { href: 'mailto:info@stratmechstudios.com' });
                clarityEvent('click_contact');
              }}
            >
              info@stratmechstudios.com
            </a>.
          </p>
        </div>

        <div className={styles.bottom}>
          <p>&copy; 2026 StratMech Studios. All rights reserved.</p>
        </div>
      </div>

      {/* Newsletter success/error popup */}
      {popup.isOpen && (
        <div className={styles.popupOverlay} role="dialog" aria-modal="true" aria-live="polite">
          <div className={styles.popupCard}>
            {popup.phase === 'loading' ? (
              <>
                <div className={styles.popupSpinner} />
                <h3 className={styles.popupTitle}>Please wait</h3>
                <p className={styles.popupMessage}>{popup.message}</p>
              </>
            ) : (
              <>
                {popup.phase === 'success' && (
                  <div className={styles.popupSuccessIcon}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
                <h3 className={styles.popupTitle}>
                  {popup.phase === 'success' ? "You\u2019re subscribed" : 'Something went wrong'}
                </h3>
                <p className={styles.popupMessage}>
                  {popup.message}
                </p>
                <button
                  ref={popupButtonRef}
                  type="button"
                  className={styles.popupButton}
                  onClick={handlePopupOk}
                >
                  Got it
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </footer>
  );
}
